import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { ChevronLeft, LinkIcon } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Separator } from "~/components/ui/separator";
import { Skeleton } from "~/components/ui/skeleton";
import {
	grammarItemQueryOptions,
	useGenerateAIExample,
	useGrammarItem,
	useGrammarSentenceExamples,
} from "~/lib/client/grammar";
import { useSetAtom } from "jotai";
import {
	currentPageContextAtom,
	type ChatWidgetContext,
} from "~/components/practice/chat-widget";
import { generateLearnGrammarItemPageContext } from "~/lib/context-gen";
import type {
	GrammarContent,
	Source,
} from "~/lib/server/db/schema/flashcard-schema";
import { m } from "~/i18n/messages";
import { SuspenseFallback } from "~/components/ui/suspense-fallback";

function GrammarItemSkeleton() {
	return (
		<div className="max-w-screen-lg mx-auto px-3 sm:px-4 pb-6">
			<div className="lg:col-span-2 py-3">
				<Skeleton className="h-5 w-24" />
			</div>
			<div className="space-y-3 sm:space-y-4">
				{/* Header Section Skeleton */}
				<div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-4 sm:p-6 shadow-sm border border-pink-200">
					<div className="flex items-start justify-between">
						<div className="flex-1">
							<Skeleton className="h-10 w-48 mb-2" />
							<Skeleton className="h-4 w-64" />
						</div>
						<Skeleton className="h-6 w-12 rounded-full" />
					</div>
				</div>

				<div className="space-y-3 sm:space-y-4">
					{/* Meanings Section Skeleton */}
					<div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-pink-200 mb-4">
						<Skeleton className="h-6 w-32 mb-4" />
						<div className="space-y-2">
							<Skeleton className="h-4 w-full" />
							<Skeleton className="h-4 w-3/4 ml-4" />
						</div>
					</div>

					{/* Example Sentences Section Skeleton */}
					<div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-pink-200">
						<div className="flex items-center justify-between mb-4">
							<Skeleton className="h-6 w-40" />
							<Skeleton className="h-3 w-20" />
						</div>
						
						{/* Example sentences skeleton */}
						<div className="space-y-4">
							<div className="group">
								<div className="flex items-center justify-between gap-2">
									<Skeleton className="h-4 flex-1" />
									<Skeleton className="h-6 w-16 rounded-full" />
								</div>
								<Skeleton className="h-3 w-2/3 mt-1 ml-4" />
							</div>
							<div className="group">
								<div className="flex items-center justify-between gap-2">
									<Skeleton className="h-4 flex-1" />
									<Skeleton className="h-6 w-16 rounded-full" />
								</div>
								<Skeleton className="h-3 w-2/3 mt-1 ml-4" />
							</div>
							<div className="group">
								<div className="flex items-center justify-between gap-2">
									<Skeleton className="h-4 flex-1" />
									<Skeleton className="h-6 w-16 rounded-full" />
								</div>
								<Skeleton className="h-3 w-2/3 mt-1 ml-4" />
							</div>
						</div>

						{/* Generation Button Skeleton */}
						<div className="mt-4 sm:mt-6 pt-4 border-t border-pink-100">
							<Skeleton className="h-10 w-full" />
							<Skeleton className="h-3 w-3/4 mt-2" />
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export const Route = createFileRoute(
	"/app/learn/jlpt/$level/grammar/$grammarId",
)({
	component: () => (
		<SuspenseFallback
			loading={<GrammarItemSkeleton />}
			error={<div className="p-8 text-red-500">Failed to load grammar item. Please try again.</div>}
		>
			<GrammarItemComponent />
		</SuspenseFallback>
	),
	loader: async ({ context, params }) => {
		const data = await context.queryClient.ensureQueryData(
			grammarItemQueryOptions(params.grammarId),
		);
		// TODO: Handle potential changes in grammar data structure here
		console.log("Fetched grammar data:", data); // Placeholder for handling data structure changes
	},
});

function GrammarItemComponent() {
	const { grammarId, level } = Route.useParams();
	const setPageContext = useSetAtom(currentPageContextAtom);

	const { data: grammar } = useGrammarItem(grammarId);
	const { data: examples } = useGrammarSentenceExamples(grammar as Source | null);

	useEffect(() => {
		if (grammar) {
			setPageContext({
				title: grammar.reading,
				slug: `/app/learn/jlpt/${level}/grammar/${grammarId}`,
				contextData: generateLearnGrammarItemPageContext({
					grammarItem: {
						id: grammarId,
						level: grammar.level,
						reading: grammar.reading,
						content: {
							usages: grammar.content.usages,
							meaning: grammar.content.meaning,
						},
					},
				}),
			});
		}
	}, [grammar, grammarId, setPageContext, level]);

	const generateAIExample = useGenerateAIExample();
	const isGenerating = generateAIExample.isPending;

	const content = grammar.content as GrammarContent;
	const allExamples = examples || [];

	function getButtonContent(
		mutation: typeof generateAIExample,
		defaultText: string,
	) {
		if (mutation.isPending) {
			return (
				<span className="flex items-center justify-center gap-2">
					<svg
						className="animate-spin h-4 w-4 text-white"
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24"
						aria-label="Loading"
					>
						<title>Loading spinner</title>
						<circle
							className="opacity-25"
							cx="12"
							cy="12"
							r="10"
							stroke="currentColor"
							strokeWidth="4"
						/>
						<path
							className="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
						/>
					</svg>
					{m["ui.generating_label"]()}
				</span>
			);
		}
		return defaultText;
	}

	return (
		<div className="max-w-screen-lg mx-auto px-3 sm:px-4 pb-6">
			<div className="lg:col-span-2 py-3">
				<Link
					to="/app/learn/jlpt/$level"
					params={{ level }}
					className="flex items-center gap-1 text-pink-500/80 hover:text-pink-600 transition-colors text-sm"
				>
					<ChevronLeft className="w-4 h-4" />
					<span>{m["ui.back_to_list_button"]()}</span>
				</Link>
			</div>
			<div className="space-y-3 sm:space-y-4">
				{/* Header Section */}
				<div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-4 sm:p-6 shadow-sm border border-pink-200">
					<div className="flex items-start justify-between">
						<div>
							<h1 className="text-3xl sm:text-4xl font-bold text-pink-800">
								{grammar.reading}
							</h1>
							<p className="text-pink-600/70 mt-2 text-sm sm:text-base">
								{grammar.content.usages?.join(", ") || ""}
							</p>
						</div>
						<span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-xs font-medium">
							{grammar.level.toUpperCase()}
						</span>
					</div>
				</div>

				<div className="space-y-3 sm:space-y-4">
					{/* Meanings Section */}
					<div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-pink-200 mb-4">
						<h2 className="text-lg sm:text-xl font-semibold text-pink-700 mb-2">
							{m["details_view.meanings_usage_title"]()}
						</h2>
						<ul className="space-y-2">
							<li
								key={`meaning-${content.meaning.meaning.substring(0, 20)}`}
								className="text-pink-800 text-sm sm:text-base"
							>
								• {content.meaning.meaning.trim()}
								{content.meaning.example && (
									<p className="text-pink-600/70 text-sm sm:text-base ml-4">
										{m["details_view.usage_label"]()}
										{content.meaning.example}
									</p>
								)}
							</li>
						</ul>
					</div>

					{/* Example Sentences Section */}
					<div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-pink-200">
						<div className="flex items-center justify-between mb-2">
							<h2 className="text-lg sm:text-xl font-semibold text-pink-700">
								{m["dictionary.example_sentences.title"]()}
							</h2>
							<span className="text-xs text-muted-foreground">
								{m["dictionary.example_sentences.source_label"]()}
							</span>
						</div>
						{/* Example sentences display */}
						{allExamples.length > 0 ? (
							<div className="space-y-4">
								{allExamples.map((p) => (
									<div key={p.sentence} className="group">
										<div className="flex items-center justify-between gap-2">
											<p
												className="text-pink-800 font-normal text-sm sm:text-base [&_em]:text-pink-500 [&_em]:font-semibold [&_em]:not-italic [&_em]:underline"
												// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
												dangerouslySetInnerHTML={{
													__html:
														p.sentence?.replaceAll(
															grammar.reading.replace(/〜/, ""),
															(match: string) => `<em>${match}</em>`,
														) ?? "",
												}}
											/>
											{p.source !== "AI" ? (
												<a
													href={p.source}
													className="flex gap-1 items-center px-2 rounded-full text-xs text-pink-600/70 bg-pink-50 border border-pink-300"
													target="_blank"
													rel="noreferrer"
												>
													{m["ui.open_link_button"]()}
													<LinkIcon className="size-3" />
												</a>
											) : (
												<div className="flex gap-1 items-center px-2 rounded-full text-xs text-pink-600/70 bg-pink-50 border border-pink-300">
													{m["ui.ai_label"]()}
												</div>
											)}
										</div>
										<ul className="text-pink-600/80 text-xs sm:text-sm mt-1 pl-4">
											{p.translation}
										</ul>
									</div>
								))}
							</div>
						) : (
							<p className="text-pink-600/80 text-sm">
								{m["dictionary.example_sentences.none_available_message"]()}
							</p>
						)}

						{/* Generation Buttons and Errors */}
						<div className="space-y-3 mt-4 sm:mt-6 pt-4 border-t border-pink-100">
							{/* AI Generation Button */}
							<Button
								onClick={() =>
									generateAIExample.mutate({
										data: {
											grammarId,
											readings: [grammar.reading],
											word: grammar.reading,
											meaning: {
												meaning: content.meaning.meaning,
												example: content.meaning.example || "", // Provide default empty string if example is undefined
											},
											level: grammar.level,
										},
									})
								}
								disabled={isGenerating}
								className="w-full text-sm"
								variant="outline"
							>
								{getButtonContent(
									generateAIExample,
									m["dictionary.example_sentences.generate_ai_button"](),
								)}
							</Button>

							{/* Error Displays */}
							{generateAIExample.error && (
								<div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-xs sm:text-sm">
									{m["errors.ai_error_prefix"]()}{" "}
									{generateAIExample.error.message}
								</div>
							)}

							{/* Note about generated examples */}
							<p className="text-xs text-pink-600/70 pt-2">
								{m["dictionary.example_sentences.accuracy_note_text"]()}
								with other resources. Massif examples come from a large corpus
								and may be more reliable.
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
